import React, { ChangeEvent } from 'react';

import { css, cx } from '@emotion/css';
import { TextField } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Queries } from '@/queries/Queries.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { MessageBoxFileUpload } from '@/uiKit/molecules/chat/MessageBoxFileUpload.tsx';
import { MessageBoxSendButton } from '@/uiKit/molecules/chat/MessageBoxSendButton.tsx';
import { MessageBoxVisibilitySelector } from '@/uiKit/molecules/chat/MessageBoxVisibilitySelector.tsx';
import { MessageData } from '@/uiKit/molecules/chat/MessageDataSchema.ts';

export type MessageTextBoxProps = {
  className?: string;
};

export const MessageTextBox: React.FC<MessageTextBoxProps> = ({
  className,
}) => {
  const { t } = useTranslation();
  const me = Queries.user.usePractitionerMe({});
  const patientId = usePatientIdFromURL();
  const styles = useStyles(makeStyles);
  const updateDraftMessage = Queries.chat.useUpdateDraftMessage();
  const formContext = useFormContext<MessageData>();
  const formValues = useWatch({
    control: formContext.control,
  });

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.top}>
        <MessageBoxVisibilitySelector />
        <MessageBoxFileUpload />
      </div>
      <div className={styles.bottom}>
        <TextField
          id="text-area-nestable"
          value={formValues.contentText}
          className={styles.textField}
          multiline
          minRows={6}
          maxRows={6}
          variant="standard"
          fullWidth
          slotProps={{ input: { disableUnderline: true } }}
          placeholder={
            formValues.visibility === 'practitioners'
              ? t('messaging.toPractitionerPlaceholder')
              : t('messaging.toAllPlaceholder')
          }
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            formContext.setValue('contentText', value, {
              shouldValidate: true,
            });
            updateDraftMessage.mutate({
              patientId,
              practitionerId: me.id,
              message: value,
            });
          }}
        />
        <MessageBoxSendButton className={styles.sendButton} />
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(8),
    gap: theme.spacing(8),
    borderTop: `1px solid`,
    borderColor: theme.palette.divider,
  }),
  bottom: css({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
  }),
  top: css({
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(4),
  }),
  textField: css({
    scrollbarColor: 'lightgrey transparent',
    '::-webkit-scrollbar': {
      backgroundColor: 'transparent',
    },
  }),
  sendButton: css({
    position: 'fixed',
    bottom: theme.spacing(8),
    right: theme.spacing(8),
  }),
});
